'use client';

import './modal.scss';

import {
  Close as RdxClose,
  Content as RdxContent,
  Overlay as RdxOverlay,
  Portal as RdxPortal,
  Root as RdxRoot,
  DialogProps as RdxRootProps,
  Title as RdxTitle,
  Trigger as RdxTrigger,
} from '@radix-ui/react-dialog';
import { ComponentPropsWithoutRef, type JSX, forwardRef } from 'react';

import { CloseButton } from '../../atoms/close-button/close-button';
import { cn } from '../../utils/helpers';

export type ModalProps = ComponentPropsWithoutRef<'div'> & {
  defaultOpen?: RdxRootProps['defaultOpen'];
  modal?: RdxRootProps['modal'];
  onClose?: () => void;
  onOpen?: () => void;
  onOpenChange?: RdxRootProps['onOpenChange'];
  open?: RdxRootProps['open'];
};

export const Modal: React.FC<ModalProps> = ({
  children,
  onClose,
  onOpen,
  onOpenChange,
  ...rdxProps
}) => (
  <RdxRoot
    onOpenChange={(open) => {
      if (onOpenChange) {
        onOpenChange(open);
      }
      if (onOpen && open) {
        onOpen();
      } else if (onClose && !open) {
        onClose();
      }
    }}
    {...rdxProps}
  >
    {children}
  </RdxRoot>
);

/**
 * Modal content.
 */
export type ModalContentProps = ComponentPropsWithoutRef<typeof RdxContent> & {
  className?: string;
};

export const ModalContent = forwardRef<HTMLDivElement, ModalContentProps>(
  ({ style, className, ...props }, ref) => {
    return (
      <RdxPortal>
        <RdxOverlay className="Modal__overlay" />
        <RdxContent
          ref={ref}
          aria-describedby={undefined}
          className={cn('Modal__content', {
            [`${className}`]: !!className,
          })}
          style={{
            ...style,
          }}
          {...props}
          // @ts-expect-error fixes issue with tabbing in form
          tabIndex={false}
        >
          {props.children}
        </RdxContent>
      </RdxPortal>
    );
  },
);

/**
 * Modal trigger.
 */
export type ModalTriggerProps = {
  children: JSX.Element;
};
export const ModalTrigger: React.FC<ModalTriggerProps> = ({ children }) => {
  return <RdxTrigger asChild>{children}</RdxTrigger>;
};

/**
 * Modal close.
 */
export const ModalClose = RdxClose;

/**
 * Modal title.
 */
export const ModalTitle = RdxTitle;

/**
 * Modal close button.
 */
export const ModalCloseButton: React.FC<
  React.ButtonHTMLAttributes<HTMLButtonElement> & {
    className?: string;
    name?: string;
  }
> = ({ className, name, ...props }) => {
  return (
    <ModalClose asChild>
      <CloseButton
        className={cn('Modal__close', {
          [`${className}`]: !!className,
        })}
        aria-label={`Close ${name}`}
        {...props}
      />
    </ModalClose>
  );
};
